import SectionCard from '../ui/SectionCard';
import Icon from '../ui/Icon';
import TextField from '../ui/TextField';
import PhoneField from '../ui/PhoneField';
import {
  CUSTOMER_FIELDS,
  CUSTOMER_FIELD_NAMES,
  REQUIRED_CUSTOMER_FIELDS,
} from '../state/checkoutSchema';

/**
 * Customer detail inputs, rendered FROM the schema rather than hand-written per field.
 *
 * That is what guarantees no field can drift: previously six near-identical blocks each
 * repeated their own label, autoComplete token, error markup, and class strings, so an
 * accessibility fix or an autofill token had to be applied six times and could be missed once.
 *
 * @param {object} props
 * @param {object} props.values      { [fieldName]: string }
 * @param {object} props.errors      { [fieldName]: string }
 * @param {Function} props.onChange  (name, value) => void
 * @param {Function} props.onBlur    (name, value) => void
 * @param {object} [props.fieldRefs] mutable ref map so blockers can focus a field
 */
export default function CustomerDetailsSection({ values, errors, onChange, onBlur, fieldRefs }) {
  const registerRef = (name) => (el) => {
    if (fieldRefs) fieldRefs.current[name] = el;
  };

  const renderField = (name) => {
    const field = CUSTOMER_FIELDS[name];
    const Component = field.mask === 'us-phone' ? PhoneField : TextField;

    return (
      <Component
        key={name}
        name={name}
        field={field}
        value={values[name] ?? ''}
        error={errors[name]}
        onChange={onChange}
        onBlur={onBlur}
        inputRef={registerRef(name)}
      />
    );
  };

  // Two columns on wider screens, preserving the original pairing.
  const [first, last, email, phone, website, company] = CUSTOMER_FIELD_NAMES;

  /*
    DERIVED, never stored. The handoff is explicit that this must not become a `complete` flag
    in state, and the reason is the same one that motivated the reducer rewrite: a stored flag
    is a second source of truth that goes stale the moment a field is cleared.

    Read straight off REQUIRED_CUSTOMER_FIELDS so adding a required field to the schema makes
    the check stricter automatically, with nothing here to update.
  */
  const isComplete =
    REQUIRED_CUSTOMER_FIELDS.every((name) => (values[name] || '').trim()) &&
    REQUIRED_CUSTOMER_FIELDS.every((name) => !errors[name]);

  return (
    <SectionCard
      title="Your details"
      stepNumber={1}
      description="How we reach you about your new website. Starred fields are required."
      status={
        isComplete && (
          // Not aria-hidden: "your details are complete" is real information, and a sighted
          // user gets it from the check mark. Icon renders the title into <title> in the SVG.
          <Icon
            name="checkCircle"
            title="Your details are complete"
            className="h-4 w-4 text-success-600"
          />
        )
      }
    >
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {renderField(first)}
        {renderField(last)}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {renderField(email)}
        {renderField(phone)}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {renderField(website)}
        {renderField(company)}
      </div>
    </SectionCard>
  );
}
